import { useState, useEffect, useCallback } from 'react';
import { getMyTickets } from '../../api/supportTicketApi';
import { fmtIST } from '../../utils/dateFormat';
import CreateSupportTicketModal from './CreateSupportTicketModal';
import ViewSupportTicketModal from './ViewSupportTicketModal';

const cyan = '#00e5ff';

const statusColors = {
  open: '#ffaa44',
  in_progress: '#66aaff',
  resolved: '#66ff99',
  closed: '#888',
};

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getMyTickets({ status: status !== 'all' ? status : undefined, page, limit: 20 })
      .then((res) => {
        setTickets(res.data.tickets);
        setTotal(res.data.total);
        setPages(res.data.pages);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h4 style={{ color: '#fff', fontWeight: 700, margin: 0 }}>Support Tickets</h4>
          <button
            onClick={() => setShowCreate(true)}
            style={{
              background: cyan,
              border: 'none',
              color: '#000',
              borderRadius: 8,
              padding: '10px 20px',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            New Ticket
          </button>
        </div>
        <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>{total} tickets</div>

        {/* Filter */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              ['all', 'All'],
              ['open', 'Open'],
              ['in_progress', 'In Progress'],
              ['resolved', 'Resolved'],
              ['closed', 'Closed'],
            ].map(([val, label]) => (
              <button
                key={val}
                onClick={() => {
                  setStatus(val);
                  setPage(1);
                }}
                style={{
                  background: status === val ? cyan : 'rgba(255,255,255,0.05)',
                  border: 'none',
                  color: status === val ? '#000' : '#888',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>Loading...</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 40 }}>No tickets found</div>
          ) : (
            tickets.map((t) => (
              <div
                key={t._id}
                onClick={() => setSelectedTicket(t)}
                style={{
                  background: '#111',
                  border: '1px solid rgba(0,229,255,0.1)',
                  borderRadius: 12,
                  padding: '16px',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,229,255,0.03)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#111')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12, gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: cyan, fontSize: 11, fontWeight: 600, marginBottom: 4, fontFamily: 'monospace' }}>
                      {t.ticketId}
                    </div>
                    <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
                    <div style={{ color: '#888', fontSize: 12 }}>{t.description.substring(0, 100)}...</div>
                  </div>
                  <span
                    style={{
                      background: `${statusColors[t.status]}20`,
                      color: statusColors[t.status],
                      border: `1px solid ${statusColors[t.status]}40`,
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.status.replace('_', ' ')}
                  </span>
                </div>
                <div style={{ color: '#555', fontSize: 11 }}>Created: {fmtIST(t.createdAt)}</div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
            {Array.from({ length: Math.min(pages, 5) }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                  border: 'none',
                  color: p === page ? '#000' : '#888',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreate && <CreateSupportTicketModal onClose={() => { setShowCreate(false); load(); }} />}
      {selectedTicket && <ViewSupportTicketModal ticket={selectedTicket} onClose={() => { setSelectedTicket(null); load(); }} />}
    </div>
  );
};

export default SupportTickets;
